import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Upload, PackageSearch, ImagePlus } from 'lucide-react';
import { normalizeImageUrl } from '../../pages/CatalogPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Weight Loss', 'Recovery', 'Growth Hormone', 'Skin', 'Cognitive', 'Longevity', 'Solvents'];

const emptyForm = () => ({ name: '', code: '', category: 'Cognitive', strength_mg: '', vials_per_case: 10, price_per_case: '', available: true, image_url: '' });

const AdminWholesaleCatalogTab = ({ adminFetch }) => {
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [editId, setEditId] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm());
  const [saving, setSaving] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [showImport, setShowImport] = React.useState(false);
  const [csv, setCsv] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const uploadImage = async (fileObj) => {
    if (!fileObj) return;
    if (!fileObj.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', fileObj);
      const r = await adminFetch(`${API_URL}/api/admin/wholesale-products/upload-image`, { method: 'POST', body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Upload failed');
      setForm((f) => ({ ...f, image_url: d.image_url }));
      toast.success('Image uploaded');
    } catch (e) { toast.error(e.message); } finally { setUploading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/wholesale-products`);
      const d = await r.json();
      setProducts(d.products || []);
    } finally { setLoading(false); }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setShowForm(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditId(p.id); setShowForm(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, vials_per_case: parseInt(form.vials_per_case) || 0, price_per_case: parseFloat(form.price_per_case) || 0 };
      const url = editId ? `${API_URL}/api/admin/wholesale-products/${editId}` : `${API_URL}/api/admin/wholesale-products`;
      const r = await adminFetch(url, { method: editId ? 'PUT' : 'POST', body: JSON.stringify(payload) });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Save failed'); }
      toast.success(editId ? 'Product updated' : 'Product added');
      setShowForm(false); load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      const r = await adminFetch(`${API_URL}/api/admin/wholesale-products/${p.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Deleted'); load();
    } catch (e) { toast.error(e.message); }
  };

  const runImport = async () => {
    if (!csv.trim()) { toast.error('Paste CSV first'); return; }
    setSaving(true);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/wholesale-products/import`, { method: 'POST', body: JSON.stringify({ csv }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Import failed');
      toast.success(`Imported ${d.created} products${d.errors?.length ? ` (${d.errors.length} skipped)` : ''}`);
      setShowImport(false); setCsv(''); load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const filtered = products.filter((p) => {
    const q = search.toLowerCase();
    return !q || p.name.toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-wholesale-catalog">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-900">Wholesale Catalog</h2>
          <p className="text-sm text-slate-500">Secret, case-based bulk catalog — visible only to customers you flag as Wholesale.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="wholesale-import-btn"><Upload className="h-4 w-4 mr-1" /> Import CSV</Button>
          <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" onClick={openNew} data-testid="wholesale-add-btn"><Plus className="h-4 w-4 mr-1" /> Add Product</Button>
        </div>
      </div>

      <Input placeholder="Search name, code, category…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-4 max-w-md" data-testid="wholesale-search" />

      {loading ? (
        <p className="text-slate-400 text-sm py-8">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><PackageSearch className="h-10 w-10 mx-auto mb-2 opacity-50" /><p>No products yet. Add one or import a CSV.</p></div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Strength</th>
                <th className="text-right px-3 py-2">Vials/case</th><th className="text-right px-3 py-2">Price/case</th>
                <th className="text-center px-3 py-2">Available</th><th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((p) => (
                <tr key={p.id} data-testid={`wholesale-row-${p.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{p.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{p.code}</td>
                  <td className="px-3 py-2">{p.category}</td>
                  <td className="px-3 py-2">{p.strength_mg}</td>
                  <td className="px-3 py-2 text-right">{p.vials_per_case}</td>
                  <td className="px-3 py-2 text-right">${p.price_per_case.toFixed(2)}</td>
                  <td className="px-3 py-2 text-center">{p.available ? <span className="text-green-600">Yes</span> : <span className="text-slate-400">No</span>}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`wholesale-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => del(p)} data-testid={`wholesale-delete-${p.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="wholesale-form-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-slate-900">{editId ? 'Edit' : 'Add'} Product</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-3">
              <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wholesale-form-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Code / SKU</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="wholesale-form-code" /></div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="wholesale-form-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Strength (e.g. 10mg)</Label><Input value={form.strength_mg} onChange={(e) => setForm({ ...form, strength_mg: e.target.value })} data-testid="wholesale-form-strength" /></div>
                <div><Label className="text-xs">Vials / case</Label><Input type="number" value={form.vials_per_case} onChange={(e) => setForm({ ...form, vials_per_case: e.target.value })} data-testid="wholesale-form-vials" /></div>
                <div><Label className="text-xs">Price / case ($)</Label><Input type="number" value={form.price_per_case} onChange={(e) => setForm({ ...form, price_per_case: e.target.value })} data-testid="wholesale-form-price" /></div>
              </div>
              <div>
                <Label className="text-xs">Product image</Label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadImage(e.dataTransfer.files?.[0]); }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`mt-1 flex items-center gap-3 rounded-lg border-2 border-dashed p-3 cursor-pointer transition-colors ${dragOver ? 'border-teal-500 bg-teal-50' : 'border-slate-300 hover:border-teal-400'}`}
                  data-testid="wholesale-image-dropzone"
                >
                  <div className="h-16 w-16 shrink-0 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
                    {form.image_url ? (
                      <img src={normalizeImageUrl(form.image_url)} alt="preview" className="h-full w-full object-contain" data-testid="wholesale-image-preview" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <ImagePlus className="h-6 w-6 text-slate-300" />
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {uploading ? 'Uploading…' : <><span className="font-medium text-slate-700">Drag &amp; drop</span> an image here, or <span className="text-teal-700 font-medium">click to browse</span>. No Google Drive needed.</>}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="wholesale-image-file" />
                </div>
                <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="…or paste an image URL (Google Drive links auto-fixed)" className="mt-2 text-xs" data-testid="wholesale-form-image" />
                {form.image_url && (
                  <button type="button" onClick={() => setForm({ ...form, image_url: '' })} className="text-[11px] text-red-500 mt-1 hover:underline" data-testid="wholesale-image-clear">Remove image</button>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} data-testid="wholesale-form-available" /> In stock / available
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white" disabled={saving} onClick={save} data-testid="wholesale-form-save">{saving ? 'Saving…' : 'Save'}</Button>
            </div>
          </div>
        </div>
      )}

      {showImport && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="wholesale-import-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold text-slate-900">Import products (CSV)</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowImport(false)}><X className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-slate-500 mb-2">Header row required: <code>name,code,category,strength_mg,vials_per_case,price_per_case,available,image_url</code></p>
            <textarea className="w-full h-48 border rounded-lg p-2 text-xs font-mono" value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="name,code,category,strength_mg,vials_per_case,price_per_case,available,image_url&#10;Cerebrolysin,CBL60,Cognitive,60mg,10,148.50,yes," data-testid="wholesale-import-textarea" />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" onClick={() => setShowImport(false)}>Cancel</Button>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white" disabled={saving} onClick={runImport} data-testid="wholesale-import-run">{saving ? 'Importing…' : 'Import'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWholesaleCatalogTab;
